import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getErrorHttpMessage } from "utils";
import { StoreType } from "types/genericTypes";
import { useIntl } from "react-intl";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  activarAlmacenHttp,
  createAlmacenHttp,
  desactivarAlmacenHttp,
  editarAlmacenHttp,
  getAlmacenesHttp,
} from "actions/almacen";

export const useAlmacen = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const [procesando, setProcesando] = useState<boolean>(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [mensajeAlert, setMensajeAlert] = useState("");
  const [errorLogin, setErrorLogin] = useState(false);
  const handleisAlertOpen = () => setIsAlertOpen(true);
  const handleisAlertClose = () => setIsAlertOpen(false);

  /* CREAR CATEGORÍA */
  const [procesandoAlmacen, setProcesandoAlmacen] = useState<boolean>(false);
  const [isAlertOpenCrearAlmacen, setIsAlertOpenCrearAlmacen] = useState(false);
  const handleisAlertCrearAlmacenOpen = () => setIsAlertOpenCrearAlmacen(true);
  const handleisAlertCrearAlmacenClose = () => setIsAlertOpenCrearAlmacen(false);

  const [verEditarAlmacen, setVerEditarAlmacen] = useState<any>(null);
  const [isAlertOpenEditarAlmacen, setIsAlertOpenEditarAlmacen] = useState(false);
  const handleisAlertOpenEditarAlmacen = () => setIsAlertOpenEditarAlmacen(true);
  const handleisAlertCloseEditarAlmacen = () => setIsAlertOpenEditarAlmacen(false);

  const [almacenes, setAlmacenes] = useState<any[]>([]);

  // Estados para la paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  //para buscador
  const [buscador, setBuscador] = useState("");
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null);

  //Debounce para evitar muchas peticiones
  const handleBuscadorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBuscador(value);

    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
    const timeout = setTimeout(() => {
      getAlmacenes({ search: value });
    }, 500);
    setDebounceTimeout(timeout);
  };

  const formik = useFormik({
    initialValues: {
      clave: "",
      nombre: "",
      descripcion: "",
    },
    validationSchema: Yup.object({
      clave: Yup.string(),
      nombre: Yup.string().required(intl.formatMessage({ id: "input_validation_requerido" })),
      descripcion: Yup.string().required(intl.formatMessage({ id: "input_validation_requerido" })),
    }),
    onSubmit: async (values) => {
      //console.log("Formulario enviado:", values);
    },
  });

  const isFieldValid = (fieldName: keyof typeof formik.values) => {
    return (
      formik.touched[fieldName] &&
      !formik.errors[fieldName] &&
      formik.values[fieldName] &&
      formik.values[fieldName] !== ""
    );
  };

  const getFieldColor = (fieldName: keyof typeof formik.values) => {
    return isFieldValid(fieldName) ? "#00AB16" : undefined;
  };

  const getAlmacenes = useCallback(
    async (params?: { search?: string; fecha1?: string; fecha2?: string }) => {
      try {
        setProcesando(true);
        const unidadesMedida = await getAlmacenesHttp(params?.search);
        setAlmacenes(unidadesMedida);
        setProcesando(false);
      } catch (error) {
        setProcesando(false);
        const message = getErrorHttpMessage(error);
        setMensajeAlert(message || intl.formatMessage({ id: "get_elementos_error" }));
        handleisAlertOpen();
      }
    },
    []
  );

  const createAlmacen = async (datos: any) => {
    try {
      setProcesandoAlmacen(true);
      const response: any = await createAlmacenHttp(datos);
      setAlmacenes((prevAlmacens: any[]) => [{ ...response, productos: 0 }, ...prevAlmacens]);

      setMensajeAlert(intl.formatMessage({ id: "general_añadido_correctamente" }));
      formik.resetForm();
      handleisAlertCrearAlmacenClose();
      handleisAlertOpen();
      setProcesandoAlmacen(false);
    } catch (error) {
      setProcesandoAlmacen(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "error_añadir_general" }));
      handleisAlertOpen();
    }
  };
  const editarAlmacen = async (datos: any) => {
    try {
      setProcesandoAlmacen(true);
      const response: any = await editarAlmacenHttp(datos);

      setAlmacenes((prevAlmacens: any[]) =>
        prevAlmacens.map((c) =>
          c.id === response?.id
            ? {
                ...c,
                clave: response?.clave,
                nombre: response?.nombre,
                descripcion: response?.descripcion,
              }
            : c
        )
      );
      setMensajeAlert(intl.formatMessage({ id: "general_editado_correctamente" }));
      formik.resetForm();
      handleisAlertCloseEditarAlmacen();
      handleisAlertOpen();
      setProcesandoAlmacen(false);
    } catch (error) {
      setProcesandoAlmacen(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "general_editado_error" }));
      handleisAlertOpen();
    }
  };

  const desactivarAlmacen = async (datos: any) => {
    try {
      setProcesando(true);
      const Almacen: any = await desactivarAlmacenHttp(datos);
      setAlmacenes((prevTipoProductos: any[]) => {
        return prevTipoProductos.map((c) => {
          if (c.id === Almacen.id) {
            return {
              ...c,
              estatus: Almacen.estatus,
            };
          }
          return c;
        });
      });
      setMensajeAlert(intl.formatMessage({ id: "general_desactivado_correctamente" }));
      handleisAlertOpen();
      setProcesando(false);
    } catch (error) {
      setProcesando(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "general_desactivado_error" }));
      handleisAlertOpen();
    }
  };
  const activarAlmacen = async (datos: any) => {
    try {
      setProcesando(true);
      const Almacen: any = await activarAlmacenHttp(datos);
      setAlmacenes((prevAlmacens: any[]) => {
        return prevAlmacens.map((t) => {
          if (t.id === Almacen.id) {
            return {
              ...t,
              estatus: Almacen.estatus,
            };
          }
          return t;
        });
      });
      setMensajeAlert(intl.formatMessage({ id: "general_activado_correctamente" }));
      handleisAlertOpen();
      setProcesando(false);
    } catch (error) {
      setProcesando(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "general_activado_error" }));
      handleisAlertOpen();
    }
  };

  useEffect(() => {
    if (verEditarAlmacen) {
      formik.setValues({
        clave: verEditarAlmacen?.clave || "",
        nombre: verEditarAlmacen?.nombre || "",
        descripcion: verEditarAlmacen?.descripcion || "",
      });
    }
  }, [verEditarAlmacen]);

  useEffect(() => {
    getAlmacenes();
  }, []);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const almacenesPaginados = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return almacenes?.slice(startIndex, endIndex) || [];
  }, [almacenes, page, rowsPerPage]);

  return {
    intl,
    formik,
    procesando,
    isAlertOpen,
    mensajeAlert,
    handleisAlertClose,
    almacenes,
    //crear categoria
    isAlertOpenCrearAlmacen,
    handleisAlertCrearAlmacenOpen,
    handleisAlertCrearAlmacenClose,
    getFieldColor,
    procesandoAlmacen,
    createAlmacen,
    desactivarAlmacen,
    activarAlmacen,
    editarAlmacen,
    verEditarAlmacen,
    isAlertOpenEditarAlmacen,
    handleisAlertOpenEditarAlmacen,
    handleisAlertCloseEditarAlmacen,
    setVerEditarAlmacen,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    buscador,
    handleBuscadorChange,
    almacenesPaginados,
  };
};
